<script setup>
import { computed, ref, watch } from 'vue'
import { Button } from '@drama/ui'
import { Check, Copy } from '@lucide/vue'
import { useI18n } from 'vue-i18n'

let markdownRendererPromise

function loadMarkdownRenderer() {
  if (markdownRendererPromise === undefined) {
    markdownRendererPromise = import('markdown-it').then(({ default: MarkdownIt }) => (
      new MarkdownIt({ html: false, breaks: true, linkify: false })
    ))
  }
  return markdownRendererPromise
}

const props = defineProps({
  text: { type: String, required: true },
  copyText: { type: String, required: true },
  label: { type: String, required: true },
  collapseOver: { type: Number, default: 600 },
  markdown: { type: Boolean, default: false },
})

const { t } = useI18n()

const expanded = ref(false)
const copied = ref(false)
const copyFailed = ref(false)

const collapsible = computed(() => props.copyText.length > props.collapseOver)
const open = computed(() => expanded.value || !collapsible.value)
const renderedMarkdown = ref('')

watch(
  () => props.markdown ? props.text : '',
  async (text, _, onCleanup) => {
    let active = true
    onCleanup(() => {
      active = false
    })
    if (text === '') {
      renderedMarkdown.value = ''
      return
    }
    const renderer = await loadMarkdownRenderer()
    if (active) renderedMarkdown.value = renderer.render(text)
  },
  { immediate: true },
)

async function copy() {
  copied.value = false
  copyFailed.value = false
  try {
    await navigator.clipboard.writeText(props.copyText)
    copied.value = true
  } catch {
    copyFailed.value = true
  }
}
</script>

<template>
  <div class="long-text">
    <header class="long-text__head">
      <span class="long-text__label">{{ label }}</span>
      <span
        v-if="collapsible"
        class="long-text__size"
      >{{ t('visualGameAdmin.llm.calls.longText.size', { chars: copyText.length }) }}</span>
      <div class="long-text__actions">
        <Button
          v-if="collapsible"
          variant="ghost"
          size="sm"
          @click="expanded = !expanded"
        >
          {{ expanded ? t('visualGameAdmin.llm.calls.longText.collapse') : t('visualGameAdmin.llm.calls.longText.expand') }}
        </Button>
        <Button
          class="long-text__copy"
          variant="ghost"
          size="sm"
          :title="copied ? t('visualGameAdmin.llm.calls.longText.copied') : t('visualGameAdmin.llm.calls.longText.copy')"
          :aria-label="copied ? t('visualGameAdmin.llm.calls.longText.copied') : t('visualGameAdmin.llm.calls.longText.copy')"
          @click="copy"
        >
          <Check v-if="copied" :size="14" aria-hidden="true" />
          <Copy v-else :size="14" aria-hidden="true" />
        </Button>
      </div>
    </header>

    <div
      v-if="markdown"
      class="long-text__body"
      :class="{ 'is-collapsed': !open, 'long-text__markdown': markdown }"
      v-html="renderedMarkdown"
    />

    <pre
      v-else
      class="long-text__body long-text__raw"
      :class="{ 'is-collapsed': !open }"
    >{{ text }}</pre>

    <p
      v-if="copyFailed"
      class="vga-error long-text__note"
      role="alert"
    >
      {{ t('visualGameAdmin.llm.calls.longText.copyFailed') }}
    </p>
  </div>
</template>

<style scoped>
.long-text {
  display: grid;
  gap: var(--drama-space-sm);
  min-width: 0;
}

.long-text__head {
  display: flex;
  align-items: center;
  gap: var(--drama-space-md);
}

.long-text__label {
  color: var(--drama-color-text);
  font-size: var(--drama-font-size-sm);
  font-weight: var(--drama-font-weight-medium);
}

.long-text__size {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.long-text__actions {
  display: flex;
  margin-left: auto;
  gap: var(--drama-space-xs);
}

.long-text__copy {
  opacity: 0;
  transition: opacity 160ms ease;
}

.long-text:hover .long-text__copy,
.long-text:focus-within .long-text__copy {
  opacity: 1;
}

.long-text__body {
  position: relative;
  overflow: auto;
  max-height: 60vh;
  margin: 0;
  padding: var(--drama-space-md);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
  color: var(--drama-color-text);
  font-size: var(--drama-font-size-xs);
  line-height: 1.6;
  overflow-wrap: anywhere;
}

.long-text__raw {
  font-family: var(--vgs-font-mono);
  white-space: pre-wrap;
}

.long-text__body.is-collapsed {
  overflow: hidden;
  max-height: 8.5rem;
  mask-image: linear-gradient(to bottom, #000 60%, transparent 100%);
}

.long-text__markdown :deep(:first-child) {
  margin-top: 0;
}

.long-text__markdown :deep(:last-child) {
  margin-bottom: 0;
}

.long-text__markdown :deep(h1),
.long-text__markdown :deep(h2),
.long-text__markdown :deep(h3),
.long-text__markdown :deep(h4) {
  margin: var(--drama-space-lg) 0 var(--drama-space-sm);
  color: var(--drama-color-text);
  font-family: var(--drama-font-family-heading);
}

.long-text__markdown :deep(p),
.long-text__markdown :deep(ul),
.long-text__markdown :deep(ol),
.long-text__markdown :deep(blockquote),
.long-text__markdown :deep(pre) {
  margin: 0 0 var(--drama-space-md);
}

.long-text__markdown :deep(pre) {
  overflow: auto;
  padding: var(--drama-space-md);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-bg);
}

.long-text__markdown :deep(code) {
  font-family: var(--vgs-font-mono);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.long-text__markdown :deep(a) {
  color: var(--drama-color-primary);
}

.long-text__note {
  margin: 0;
  font-size: var(--drama-font-size-xs);
}

@media (hover: none) {
  .long-text__copy {
    opacity: 1;
  }
}
</style>
